import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

/**
 * Generic edit dialog. Props:
 *  - open: boolean
 *  - title: string
 *  - fields: [{ name, label, type: 'text'|'textarea'|'select'|'number', options?, required?, placeholder? }]
 *  - initial: object of initial values
 *  - onClose: () => void
 *  - onSubmit: (values) => Promise<void>
 *  - submitLabel: string
 */
const EditDialog = ({ open, title, fields, initial, onClose, onSubmit, submitLabel = 'Save' }) => {
  const [values, setValues] = useState(initial || {});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setValues(initial || {});
      setError('');
    }
  }, [open, initial]);

  if (!open) return null;

  const handleChange = (name, val) => setValues(v => ({ ...v, [name]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await onSubmit(values);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white border border-[#e9e0cc] w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e9e0cc]">
          <h3 className="font-display text-2xl text-[#2a1a45]">{title}</h3>
          <button onClick={onClose} className="text-[#5a534a] hover:text-[#3a2359]"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {error && (
            <div className="text-sm text-[#7a1c1c] bg-[#fdecec] border border-[#f0c4c4] p-3">{error}</div>
          )}
          {fields.map((f) => (
            <div key={f.name}>
              <label className="block text-xs tracking-wider uppercase text-[#5a534a] mb-2">
                {f.label}{f.required && <span className="text-[#a68a5b]"> *</span>}
              </label>
              {f.type === 'textarea' ? (
                <textarea
                  value={values[f.name] ?? ''}
                  onChange={e => handleChange(f.name, e.target.value)}
                  required={f.required}
                  placeholder={f.placeholder}
                  rows={f.rows || 4}
                  className="w-full px-4 py-2.5 border border-[#e9e0cc] bg-[#faf7f0] focus:border-[#3a2359] focus:outline-none text-sm resize-none"
                />
              ) : f.type === 'select' ? (
                <select
                  value={values[f.name] ?? ''}
                  onChange={e => handleChange(f.name, e.target.value)}
                  required={f.required}
                  className="w-full px-4 py-2.5 border border-[#e9e0cc] bg-[#faf7f0] focus:border-[#3a2359] focus:outline-none text-sm"
                >
                  <option value="" disabled>Choose…</option>
                  {(f.options || []).map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={f.type || 'text'}
                  value={values[f.name] ?? ''}
                  onChange={e => handleChange(f.name, f.type === 'number' ? Number(e.target.value) : e.target.value)}
                  required={f.required}
                  placeholder={f.placeholder}
                  className="w-full px-4 py-2.5 border border-[#e9e0cc] bg-[#faf7f0] focus:border-[#3a2359] focus:outline-none text-sm"
                />
              )}
              {f.help && (
                <p className="mt-1.5 text-[11px] text-[#78716c] leading-snug">{f.help}</p>
              )}
            </div>
          ))}
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#e9e0cc]">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-[#e9e0cc] text-sm text-[#5a534a] hover:bg-[#faf7f0]">Cancel</button>
            <button type="submit" disabled={submitting} className="px-5 py-2 bg-[#3a2359] text-white text-sm hover:bg-[#4d2f75] disabled:opacity-60">
              {submitting ? 'Saving…' : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDialog;
